<template>
  <div>
    <transition name="backdrop-fade">
      <div v-if="isVisible" class="tc-modal-backdrop" @click="cancelEdit" />
    </transition>
    <transition name="slide-right">
      <div
        v-if="isVisible"
        class="tc-modal tc-modal-right"
        role="dialog"
        aria-labelledby="templateModalTitle"
        aria-describedby="templateModalDescription"
        @click.stop
      >
        <header
          id="templateModalTitle"
          class="tc-modal-header mpp-modal-header"
        >
          <span class="mpp-modal-headline">{{ pageTitle }}</span>
          <button
            type="button"
            class="btn-close"
            aria-label="Close modal"
            @click="cancelEdit"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill="currentColor" class="bi bi-x" viewBox="0 0 16 16">
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
            </svg>
          </button>
        </header>
        
        <section
          id="templateModalDescription"
          class="tc-modal-body"
        >
          <div class="template-form-container">
            <form @submit.prevent="createTemplate()">
              <div class="form-group">
                <label class="form-label">Template Name*</label>
                <input
                  v-model="name"
                  type="text"
                  class="mpp-input form-control p-inputtext"
                  placeholder="Enter template name"
                  required="required"
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Sender (Name)*</label>
                <input
                  :value="getDisplaySenderName()"
                  type="text"
                  class="mpp-input form-control p-inputtext"
                  :class="{ 'smtp-disabled': isSmtpSelected }"
                  placeholder="Enter sender name"
                  :disabled="isSmtpSelected"
                  @input="updateSenderName($event.target.value)"
                  required="required"
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Sender (Address)*</label>
                <input
                  :value="getSenderAddress()"
                  type="text"
                  class="mpp-input form-control p-inputtext"
                  :class="{ 'smtp-disabled': isSmtpSelected }"
                  placeholder=""
                  disabled
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Subject*</label>
                <input
                  v-model="subject"
                  type="text"
                  class="mpp-input form-control p-inputtext"
                  placeholder="Enter email subject"
                  required="required"
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Content (HTML)*</label>
                <prism-editor
                  v-model="html"
                  class="mpp-input mpp-input-editor my-editor height-300 p-inputtext"
                  :highlight="highlighter"
                  line-numbers
                />
              </div>
            </form>
          </div>
        </section>
        
        <footer class="tc-modal-footer">
          <div class="button-container">
            <Button
              label="Cancel"
              class="p-button-outlined mr-2"
              @click="cancelEdit"
            />
            <Button
              label="Save Template"
              class="mpp-btn p-button"
              :style="clientColor"
              :disabled="!canSave"
              @click="createTemplate"
            />
          </div>
        </footer>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import { API_URL } from "../api_url";
import Button from 'primevue/button';
import { shouldUseMocks, mockGetClientSettings } from "@/services/mock.service";
// import Prism Editor
import { PrismEditor } from 'vue-prism-editor';
import 'vue-prism-editor/dist/prismeditor.min.css'; // import the styles somewhere

// import highlighting library (you can use any library you want just return html string)
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism.css'; // import syntax highlighting styles

export default {
  name: 'TemplateCreate',

  components: {
    PrismEditor,
    Button
  },

  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close'],

  data() {
    return {
      on_load: false,
      load_done: true,
      html: '',
      name: '',
      subject: '',
      senderName: '',
      originalSenderName: '', // Speichert den ursprünglichen Template-Wert
      mailObj: {},
      clientDataLoaded: false,
      clientSenderEmail: null,
    };
  },
  
  computed: {
    pageTitle() {
      return this.isEditMode ? 'Edit Template' : 'Create New Template';
    },
    
    isEditMode() {
      return this.$store.state.app.current_template && this.$store.state.app.current_template.id;
    },
    
    canSave() {
      return this.name.trim() && this.subject.trim();
    },
    
    clientColor() {
      const { background_color, text_color } = this.$store.state.auth.jobFlowClient ?? {};

      if (!background_color) {
        return {};
      }

      return {
        'backgroundColor': background_color,
        'color': text_color,
        '--client-color-hover': `rgba(${this.extractRGB(background_color)}, 0.6)`
      };
    },
    
    smtpConfig() {
      return this.$store.getters['app/smtpConfig'];
    },
    
    isSmtpSelected() {
      return this.smtpConfig?.mail_provider === 'smtp';
    }
  },

  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.loadTemplateData();
      } else {
        this.resetForm();
      }
    },
    
    isSmtpSelected(newVal) {
      // Wenn SMTP-Modus geändert wird, Sender Name entsprechend setzen
      if (newVal && !this.isEditMode) {
        this.updateSenderNameForSmtp();
      }
    }
  },

  methods: {
    highlighter(code) {
      return highlight(code, languages.markup); // languages.<insert language> to return html with markup
    },

    loadTemplateData() {
      // SMTP-Konfiguration laden
      this.loadSmtpConfig();

      // Client-Daten laden
      if (!this.clientDataLoaded) {
        this.loadClientData();
        this.clientDataLoaded = true;
      }

      if (this.isEditMode) {
        const template = this.$store.state.app.current_template;
        this.name = template.template_name || '';
        this.subject = template.subject || '';
        this.senderName = template.sender_name || '';
        this.originalSenderName = template.sender_name || ''; // Ursprünglichen Wert speichern
        this.html = template.html || '';
      } else {
        // Bei SMTP den Sender Name aus der Konfiguration setzen (nur Create-Modus)
        this.updateSenderNameForSmtp();
      }
    },

    resetForm() {
      this.html = '';
      this.name = '';
      this.subject = '';
      this.senderName = '';
      this.originalSenderName = '';
    },

    cancelEdit() {
      this.resetForm();
      this.$emit('close');
    },

    getMailAddress() {
      return this.$store.state.auth.client + '@' + this.$store.state.auth.mailDomain;
    },
    
    getSenderAddress() {
      // Verwende sender_email_address von den Client-Daten
      // Falls nicht vorhanden oder leer, verwende Fallback
      if (this.clientSenderEmail) {
        return this.clientSenderEmail;
      }
      // Fallback: client + '@' + mailDomain
      return `${this.$store.state.auth.client}@${this.$store.state.auth.mailDomain}`;
    },
    
    getDisplaySenderName() {
      // Wenn SMTP ausgewählt ist, zeige den SMTP sender_name
      if (this.isSmtpSelected && this.smtpConfig?.smtp_config?.sender_name) {
        return this.smtpConfig.smtp_config.sender_name;
      }
      // Sonst zeige den aktuellen senderName Wert
      return this.senderName;
    },
    
    updateSenderName(value) {
      // Nur aktualisieren wenn SMTP nicht ausgewählt ist
      if (!this.isSmtpSelected) {
        this.senderName = value;
      }
    },

    createTemplate() {
      this.on_load = true;
      this.load_done = false;
      
      // Bestimme welcher Sender Name verwendet werden soll
      let senderNameToSend;
      
      if (this.isSmtpSelected) {
        // Bei SMTP: Im Edit-Modus ursprünglichen Wert beibehalten, im Create-Modus SMTP-Config verwenden
        if (this.isEditMode) {
          senderNameToSend = this.originalSenderName;
        } else {
          senderNameToSend = this.smtpConfig?.smtp_config?.sender_name || this.senderName;
        }
      } else {
        // Bei SES: Immer den aktuellen senderName Wert verwenden (erlaubt Bearbeitung)
        senderNameToSend = this.senderName;
      }
      
      const sendObj = {
        'name': this.name,
        'subject': this.subject,
        'senderName': senderNameToSend,
        'text': '',
        'html': this.html
      };

      const url = this.isEditMode 
        ? `https://${API_URL}/v1/template/${this.$store.state.app.current_template.id}`
        : `https://${API_URL}/v1/template/create`;
      
      const method = this.isEditMode ? 'put' : 'post';

      axios[method](url, sendObj, { 
        headers: { 'Authorization': this.$store.state.auth.sessionId } 
      })
      .then(response => {
        this.mailObj = response.data;
        this.load_done = true;
        
        // Update template in store for edit mode
        if (this.isEditMode) {
          const templateIndex = this.$store.state.app.templates.findIndex(t => t.id === this.$store.state.app.current_template.id);
          if (templateIndex !== -1) {
            this.$store.state.app.templates[templateIndex] = {
              ...this.$store.state.app.templates[templateIndex],
              template_name: this.name,
              subject: this.subject,
              sender_name: senderNameToSend,
              plain_text: '',
              html: this.html
            };
          }
        } else {
          // For create mode, refresh template list
          if (this.$parent && this.$parent.getTemplates) {
            this.$parent.getTemplates();
          }
        }
        
        this.resetForm();
        this.$emit('close');
        
        // Show success message
        this.$swal({
          title: 'Success',
          text: `Template ${this.isEditMode ? 'updated' : 'created'} successfully.`,
          icon: 'success',
          confirmButtonText: 'OK'
        });
      })
      .catch(error => {
        console.error('Error saving template:', error);
        this.load_done = true;
        
        this.$swal({
          title: 'Error',
          text: `Failed to ${this.isEditMode ? 'update' : 'create'} template.`,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      });
    },

    extractRGB(color) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `${r}, ${g}, ${b}`;
    },
    
    loadSmtpConfig() {
      // SMTP-Konfiguration laden um die aktuelle Versandmethode zu ermitteln
      this.$store.dispatch('app/fetchSmtpConfig').catch(error => {
        console.error('Error loading SMTP config in TemplateCreate:', error);
      });
    },
    
    updateSenderNameForSmtp() {
      // Bei SMTP den Sender Name aus der Konfiguration übernehmen (nur im Create-Modus)
      if (this.isSmtpSelected && !this.isEditMode && this.smtpConfig?.smtp_config?.sender_name) {
        this.senderName = this.smtpConfig.smtp_config.sender_name;
      }
    },

    async loadClientData() {
      // Client-Daten laden um sender_email_address zu erhalten
      try {
        console.log("TemplateCreate: loading client data");

        // Prüfen, ob Mock-Daten verwendet werden sollen
        if (shouldUseMocks()) {
          console.log("TemplateCreate: Using mock client data");
          const data = await mockGetClientSettings();
          console.log("TemplateCreate: Mock client data loaded:", data);
          this.clientSenderEmail = data.sender_email_address;
          return;
        }

        // Echte API-Anfrage
        console.log("TemplateCreate: Using real API for client data");
        const response = await axios.get(`https://${API_URL}/v1/client`, {
          headers: { 'Authorization': this.$store.state.auth.sessionId }
        });
        this.clientSenderEmail = response.data.sender_email_address;
        console.log("TemplateCreate: Client data loaded, sender_email_address:", this.clientSenderEmail);
      } catch (error) {
        console.error('Error loading client data in TemplateCreate:', error);
        // Bei Fehler bleibt clientSenderEmail null und der Fallback wird verwendet
      }
    },
  }
}
</script>

<style scoped>
/* Modal Styles */
.tc-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.tc-modal {
  width: 500px;
  height: auto;
  min-height: 400px;
  max-height: 80vh;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.tc-modal-right {
  width: 50%;
  height: 100vh;
  max-height: 100vh;
  position: fixed;
  top: 0;
  right: 0;
  transform: none;
  border-radius: 0;
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  z-index: 1001;
  display: flex;
  flex-direction: column;
}

.tc-modal-header {
  padding: 52px 56px 10px 66px;
  border-bottom: 1px solid #ddd !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  background: white;
  font-weight: 500;
}

.tc-modal-body {
  flex: 1;
  padding: 25px 55px 0px 50px;
  overflow-y: auto;
}

.tc-modal-footer {
  border-top: 1px solid #ddd !important;
  display: block;
  height: 88px;
  padding: 22px 56px 0 66px;
  flex-shrink: 0;
}

.button-container {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  max-width: 100%;
}

.mpp-modal-headline {
  font-size: 1.25rem;
  font-weight: 500;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
}

.btn-close:hover {
  color: #000;
}

/* Form styling for Template modal */
.template-form-container {
  max-width: 100%;
  margin: 0;
}

.form-group {
  margin-bottom: 10px;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #666;
  font-size: 14px;
}

.form-control,
.mpp-input.p-inputtext {
  width: 100%;
  padding: 6px 0;
  padding-left: 5px !important;
  border: none;
  border-left: 2px solid #43cd80 !important;
  border-bottom: 1px solid #d1d5db;
  border-radius: 0;
  font-size: 15px;
  color: #555;
  font-weight: 400;
}

.form-control::placeholder {
  color: #9ca3af;
}

.form-control:disabled {
  background-color: #f3f4f6;
  color: #6b7280;
}

.smtp-disabled {
  background-color: #f9fafb !important;
  color: #6b7280 !important;
  border-left-color: #d1d5db !important;
}

.height-300 {
  height: 300px;
}

/* Prism Editor Styles */
.prism-editor__textarea:focus {
  outline: none;
}

.my-editor {
  background: #ffffff;
  color: #333;
  border: 1px solid #d1d5db;
  font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
  font-size: 14px;
  line-height: 1.5;
  padding: 5px;
}

/* Backdrop fade transition */
.backdrop-fade-enter-active,
.backdrop-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}

/* Slide-in transition from right */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-right-enter-from {
  transform: translateX(100%);
}

.slide-right-leave-to {
  transform: translateX(100%);
}

.slide-right-enter-to {
  transform: translateX(0);
}
</style>